import {
  UserProfile,
  FeatureAccess,
  UserRole,
  SubscriptionTier
} from '@/types/user.types';
import { hasAccess, getLimit } from '@/schemas/supabase/subscriptions';

/**
 * Calculate a user's feature access based on their role and subscription tier
 *
 * @param user The user profile
 * @returns An object defining what features the user can access
 */
export function calculateFeatureAccess(user: UserProfile | null): FeatureAccess {
  if (!user) {
    // Give unauthenticated users access to free-tier features
    const freeAccessLevel: SubscriptionTier = 'free';
    
    return {
      prayers: {
        canCreate: !!hasAccess('prayers', 'create', freeAccessLevel),
        maxCount: getLimit('prayers', 'max_count', freeAccessLevel),
        canShareToCommunity: !!hasAccess('prayers', 'share_to_community', freeAccessLevel)
      },
      groups: {
        canCreate: !!hasAccess('groups', 'create', freeAccessLevel),
        canJoin: !!hasAccess('groups', 'join', freeAccessLevel),
        maxJoinCount: getLimit('groups', 'max_join_count', freeAccessLevel)
      },
      analytics: {
        hasBasicAccess: !!hasAccess('analytics', 'basic', freeAccessLevel),
        hasAdvancedAccess: !!hasAccess('analytics', 'advanced', freeAccessLevel)
      },
      reminders: {
        hasBasicAccess: !!hasAccess('reminders', 'basic', freeAccessLevel),
        hasCustomAccess: !!hasAccess('reminders', 'custom', freeAccessLevel)
      },
      aiFeatures: {
        hasBibleTranslation: !!hasAccess('ai_features', 'bible_translation', freeAccessLevel),
        hasPrayerGuides: !!hasAccess('ai_features', 'prayer_guides', freeAccessLevel)
      },
      communitySupport: {
        hasPrayerPartner: !!hasAccess('community_support', 'prayer_partner', freeAccessLevel),
        hasMentorship: !!hasAccess('community_support', 'mentorship', freeAccessLevel),
        hasSpiritualCounseling: !!hasAccess('community_support', 'spiritual_counseling', freeAccessLevel)
      },
      prayerCompanion: {
        canAccessCards: !!hasAccess('prayer_companion', 'cards_mode', freeAccessLevel),
        canAccessChat: !!hasAccess('prayer_companion', 'chat_mode', freeAccessLevel),
        canAccessPrayer: !!hasAccess('prayer_companion', 'prayer_mode', freeAccessLevel),
        canAccessRealtime: !!hasAccess('prayer_companion', 'realtime_mode', freeAccessLevel),
        dailyInteractionLimit: getLimit('prayer_companion', 'daily_interactions', freeAccessLevel)
      }
    };
  }

  // Map subscriber → basic; others → free; admins keep own role
  const subscriptionTier: SubscriptionTier =
    user.role === 'subscriber' ? 'basic' : 'free';

  const accessLevel: UserRole | SubscriptionTier = user.isAdmin
    ? user.role
    : subscriptionTier;

  return {
    prayers: {
      canCreate: !!hasAccess('prayers', 'create', accessLevel),
      maxCount: getLimit('prayers', 'max_count', accessLevel),
      canShareToCommunity: !!hasAccess(
        'prayers',
        'share_to_community',
        accessLevel
      )
    },
    groups: {
      canCreate: !!hasAccess('groups', 'create', accessLevel),
      canJoin: !!hasAccess('groups', 'join', accessLevel),
      maxJoinCount: getLimit('groups', 'max_join_count', accessLevel)
    },
    analytics: {
      hasBasicAccess: !!hasAccess('analytics', 'basic', accessLevel),
      hasAdvancedAccess: !!hasAccess('analytics', 'advanced', accessLevel)
    },
    reminders: {
      hasBasicAccess: !!hasAccess('reminders', 'basic', accessLevel),
      hasCustomAccess: !!hasAccess('reminders', 'custom', accessLevel)
    },
    aiFeatures: {
      hasBibleTranslation: !!hasAccess(
        'ai_features',
        'bible_translation',
        accessLevel
      ),
      hasPrayerGuides: !!hasAccess(
        'ai_features',
        'prayer_guides',
        accessLevel
      )
    },
    communitySupport: {
      hasPrayerPartner: !!hasAccess(
        'community_support',
        'prayer_partner',
        accessLevel
      ),
      hasMentorship: !!hasAccess(
        'community_support',
        'mentorship',
        accessLevel
      ),
      hasSpiritualCounseling: !!hasAccess(
        'community_support',
        'spiritual_counseling',
        accessLevel
      )
    },
    prayerCompanion: {
      canAccessCards: !!hasAccess(
        'prayer_companion',
        'cards_mode',
        accessLevel
      ),
      canAccessChat: !!hasAccess(
        'prayer_companion',
        'chat_mode',
        accessLevel
      ),
      canAccessPrayer: !!hasAccess(
        'prayer_companion',
        'prayer_mode',
        accessLevel
      ),
      canAccessRealtime: !!hasAccess(
        'prayer_companion',
        'realtime_mode',
        accessLevel
      ),
      dailyInteractionLimit: getLimit(
        'prayer_companion',
        'daily_interactions',
        accessLevel
      )
    }
  };
}

/**
 * Check if a user has hit their prayer limit
 *
 * @param user The user profile
 * @param currentPrayerCount The current number of prayers created by the user
 * @returns Boolean indicating if user has reached their limit
 */
export function hasReachedPrayerLimit(
  user: UserProfile | null,
  currentPrayerCount: number
): boolean {
  // Unauthenticated users can't create any prayers
  if (!user) return true;

  // Admins have unlimited prayers
  if (user.isAdmin) return false;

  // Use the same matrix we already calculated
  const { maxCount } = calculateFeatureAccess(user).prayers;
  return currentPrayerCount >= maxCount;
}
